/* eslint-disable no-underscore-dangle */
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import startCase from 'lodash/startCase';
import kebabCase from 'lodash/kebabCase';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { TooltipHost } from 'office-ui-fabric-react';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import DetailsList from '../DetailsList';
import Link from '../Link';
import { contractStatus } from '../../utils';
import styles from './ContractsTasksList.style';
import appStyles from '../../App.style';

const statusMessage = status => {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'Upload Complete';
    case 'inprogress':
    case 'processing':
      return 'Upload In Progress';
    default:
      return status;
  }
};

const formatStatus = (resourceStatus = '') => resourceStatus.toLowerCase();

const columns = [
  {
    key: 'resourceStatus',
    name: 'Status',
    fieldName: 'resourceStatus',
    iconClassName: styles.headerIcon,
    ariaLabel: 'Task',
    isIconOnly: true,
    minWidth: 14,
    maxWidth: 14,
    columnActionsMode: 0,
    onRender: (task, index) => {
      const status = formatStatus(task.resourceStatus);
      const { area } = task;
      const approvalStatus =
        area === 'contracts' || area === 'agreements'
          ? contractStatus(
              task.resource.effectiveDate,
              task.resource.terminationDate,
              task.resource.approvalStatus,
            )
          : null;

      return (
        <>
          {(area === 'contracts' ||
            area === 'agreements' ||
            area === 'contractattachments' ||
            status === 'completed') && (
            <TooltipHost
              content="Action Required"
              calloutProps={{
                gapSpace: 0,
                target: `#${`contractActionIcon${index}`}`,
              }}
            >
              <div className={styles.actionIndicator}>
                <FontIcon
                  iconName="StatusCircleExclamation"
                  ariaLabel="TextDocument"
                  id={`contractActionIcon${index}`}
                />
              </div>
            </TooltipHost>
          )}

          {(area === 'contracts' || area === 'agreements') && (
            <TooltipHost
              content={startCase(approvalStatus)}
              calloutProps={{
                gapSpace: 0,
                target: `#${`taskStatusIcon${kebabCase(task.resourceId)}`}`,
              }}
            >
              <div className={clsx(styles.iconColumn, appStyles[`${approvalStatus}Color`])}>
                <FontIcon
                  iconName="LocationDot"
                  ariaLabel="TextDocument"
                  id={`taskStatusIcon${kebabCase(task.resourceId)}`}
                />
              </div>
            </TooltipHost>
          )}

          {task.area === 'digitizedcontracts' && (
            <Spinner
              size={SpinnerSize.xSmall}
              className={clsx({
                [styles.uploadingSpinner]: status === 'processing' || status === 'inprogress',
                [styles.staticSpinner]: status === 'completed',
              })}
            />
          )}
        </>
      );
    },
  },
  {
    key: 'name',
    name: 'Task Name',
    fieldName: 'name',
    className: styles.filenameField,
    columnActionsMode: 0,
  },
  {
    key: 'resourceId',
    name: 'Resource ID',
    fieldName: 'resourceId',
    columnActionsMode: 0,
    minWidth: 100,
    maxWidth: 100,
    onRender: task => (
      <span className={styles.overflowable}>
        <TooltipHost content={`#${task.resource.contractId}`}>
          #{task.resource.contractId}
        </TooltipHost>
      </span>
    ),
  },
  {
    key: 'info',
    name: 'Info',
    fieldName: 'info',
    minWidth: 100,
    maxWidth: 100,
    columnActionsMode: 0,
    onRender: task => {
      const infoContent =
        task.area === 'digitizedcontracts' ? statusMessage(task.resourceStatus) : task.partnerName;
      return (
        <span className={styles.overflowable}>
          <TooltipHost content={infoContent}>{infoContent}</TooltipHost>
        </span>
      );
    },
  },
  {
    key: 'cta',
    name: 'CTA',
    fieldName: 'cta',
    minWidth: 185,
    maxWidth: 185,
    columnActionsMode: 0,
    className: styles.buttonColumn,
    onRender: task =>
      task.ctaLink &&
      task.ctaText && (
        <Link to={task.ctaLink} className={styles.button}>
          <Text>{task.ctaText}</Text>
          <FontIcon iconName="ChevronRight" className={styles.rightIcon} />
        </Link>
      ),
  },
];

const ContractsTasksList = ({ items, className, ...other }) => (
  <DetailsList
    items={items.map(task => {
      const { area, resource } = task;
      const status = formatStatus(task.resourceStatus);

      // call to action variables
      let ctaLink;
      let ctaText;

      switch (area) {
        // uploaded digitized PDF contracts
        case 'digitizedcontracts':
          if (status === 'completed') {
            ctaLink = `/add-contract?contractId=${task.resourceId}`;
            ctaText = 'Review and Submit';
          }
          break;

        // blockchain contracts
        case 'contracts':
          if (status === 'draft') {
            ctaLink = `/contracts/${resource.address}/add-contract`;
            ctaText = 'Review and Submit';
          }

          if (status === 'pendinginitialapproval') {
            ctaLink = `/contracts/${resource.address}/approve`;
            ctaText = 'Review and Approve';
          }

          if (status === 'pendingapproval') {
            ctaLink = `/contracts/${resource.address}/approve-edits`;
            ctaText = 'Review and Approve';
          }

          if (window.config.enableScopeFilters) {
            ctaLink = `/contracts/${resource.address}`;
          }
          break;

        case 'agreements':
          ctaLink = `/contracts/${resource.address}/royalty-fees/${task.resourceId}/approve-edits`;
          ctaText = 'Review and Approve';
          break;

        case 'contractattachments':
          ctaLink = `/contracts/${resource.address}/attachments`;
          ctaText = 'Review and Approve';
          break;

        default:
          break;
      }

      return {
        ...task,
        ctaLink,
        ctaText,
        link: ctaLink,
        key: task.key,
      };
    })}
    columns={columns}
    className={className}
    {...other}
  />
);

ContractsTasksList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      resource: PropTypes.shape({}).isRequired,
      resourceStatus: PropTypes.string.isRequired,
      resourceId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      partnerName: PropTypes.string,
    }),
  ),
  className: PropTypes.string,
};

ContractsTasksList.defaultProps = {
  items: [],
  className: '',
};

export default ContractsTasksList;
